import { ApolloClient, InMemoryCache, createHttpLink, from } from '@apollo/client';
import { setContext } from '@apollo/client/link/context';
import { onError } from '@apollo/client/link/error';
import { StrictTypedTypePolicies } from 'generated/apollo-helpers';
import { AppEnvironment } from 'services/host';

const getAuthorizationLink = (accessToken: string) =>
  setContext((_, { headers }) => {
    return {
      headers: {
        ...headers,
        Authorization: `Bearer ${accessToken}`,
      },
    };
  });

const getApolloUri = (endpointUri: string) =>
  createHttpLink({
    uri: endpointUri,
  });

/**
 * Configure Type Policies here as needed
 *
 * Reference:
 * https://www.the-guild.dev/graphql/codegen/plugins/typescript/typescript-apollo-client-helpers
 * https://www.apollographql.com/docs/react/caching/cache-configuration/#typepolicy-fields
 */
const typePolicies: StrictTypedTypePolicies = {};

export const getApolloClient = (
  accessToken: string,
  apiUri: string,
  logout: (params: { logoutParams: { returnTo: string } }) => void
) => {
  const authLink = getAuthorizationLink(accessToken);
  const httpLink = getApolloUri(apiUri);
  const errorLink = onError(({ graphQLErrors }) => {
    if (graphQLErrors) {
      graphQLErrors.forEach(error => {
        const customError = error as unknown as {
          status_code?: number;
          error?: string;
        };

        if (
          customError.status_code === 401 ||
          customError.error?.toLowerCase().includes('expired token')
        ) {
          logout({ logoutParams: { returnTo: window.location.origin } });
        }
      });
    }
  });
  const connectToDevTools = () => {
    if (typeof APP_ENVIRONMENT !== 'undefined') {
      return APP_ENVIRONMENT !== AppEnvironment.PRODUCTION;
    }
    return false;
  };
  return new ApolloClient({
    link: from([errorLink, authLink, httpLink]),
    cache: new InMemoryCache({ typePolicies }),
    connectToDevTools: connectToDevTools(),
    defaultOptions: {
      watchQuery: {
        fetchPolicy: 'cache-and-network',
      },
    },
  });
};
